import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Avatar,
  IconButton,
  CircularProgress,
  Paper,
} from "@mui/material";
import { PhotoCamera, Business } from "@mui/icons-material";
import { createBusiness, updateBusiness } from "../../../api/smb.api";

const businessTypes = [
  { value: "real_estate", label: "Real Estate" },
  { value: "insurance", label: "Insurance" },
  { value: "gym", label: "Gym / Fitness" },
  { value: "salon", label: "Salon / Beauty" },
  { value: "restaurant", label: "Restaurant / Food" },
  { value: "retail", label: "Retail / Shop" },
  { value: "healthcare", label: "Healthcare" },
  { value: "education", label: "Education" },
  { value: "consulting", label: "Consulting" },
  { value: "other", label: "Other" },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    bgcolor: "#1B1D2A",
    "& fieldset": { borderColor: "#282D51" },
    "&:hover fieldset": { borderColor: "#3B82F6" },
    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
  },
  "& .MuiInputLabel-root": { color: "#9CA3AF" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3B82F6" },
};

const BusinessInfoStep = ({ onComplete, existingBusiness }) => {
  const [form, setForm] = useState({
    business_name: existingBusiness?.business_name?.String || "",
    business_type: existingBusiness?.business_type?.String || "",
    business_description: existingBusiness?.business_description?.String || "",
    business_phone: existingBusiness?.business_phone?.String || "",
    business_email: existingBusiness?.business_email?.String || "",
    business_address: existingBusiness?.business_address?.String || "",
  });
  const [logoPreview, setLogoPreview] = useState(existingBusiness?.logo_url?.String || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.business_name.trim()) {
      setError("Business name is required");
      return;
    }
    if (!form.business_type) {
      setError("Please select a business type");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      let result;
      if (existingBusiness?.id) {
        result = await updateBusiness(existingBusiness.id, form);
      } else {
        result = await createBusiness(form);
      }

      if (result?.status && result?.data) {
        onComplete(result.data);
      } else {
        setError(result?.message || "Failed to save business info");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Tell us about your business
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={logoPreview}
            sx={{ width: 80, height: 80, bgcolor: "#282D51", cursor: "pointer" }}
            onClick={() => fileInputRef.current?.click()}
          >
            {!logoPreview && <Business sx={{ fontSize: 36, color: "#9CA3AF" }} />}
          </Avatar>
          <IconButton
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              bgcolor: "#3B82F6",
              "&:hover": { bgcolor: "#2563EB" },
              width: 28,
              height: 28,
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <PhotoCamera sx={{ fontSize: 14, color: "#fff" }} />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleLogoChange}
          />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 3, color: "#9CA3AF" }}>
        Upload your business logo (optional)
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Business Name"
          value={form.business_name}
          onChange={handleChange("business_name")}
          fullWidth
          required
          sx={inputSx}
        />
        <TextField
          label="Business Type"
          value={form.business_type}
          onChange={handleChange("business_type")}
          select
          fullWidth
          required
          sx={inputSx}
        >
          {businessTypes.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Business Description"
          value={form.business_description}
          onChange={handleChange("business_description")}
          fullWidth
          multiline
          rows={2}
          sx={inputSx}
        />
        <TextField
          label="Business Phone"
          value={form.business_phone}
          onChange={handleChange("business_phone")}
          fullWidth
          sx={inputSx}
        />
        <TextField
          label="Business Email"
          value={form.business_email}
          onChange={handleChange("business_email")}
          fullWidth
          type="email"
          sx={inputSx}
        />
        <TextField
          label="Business Address"
          value={form.business_address}
          onChange={handleChange("business_address")}
          fullWidth
          multiline
          rows={2}
          sx={inputSx}
          helperText="Physical location address (if applicable)"
          FormHelperTextProps={{ sx: { color: "#6B7280" } }}
        />

        {error && (
          <Typography sx={{ color: "#EF4444", fontSize: "0.875rem" }}>{error}</Typography>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          fullWidth
          sx={{
            mt: 1,
            py: 1.5,
            bgcolor: "#3B82F6",
            "&:hover": { bgcolor: "#2563EB" },
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          {isSubmitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Continue"}
        </Button>
      </Box>
    </Paper>
  );
};

export default BusinessInfoStep;
